import { Component, createRef } from 'react'
import cx from 'classnames'
import { autobind } from 'core-decorators'
import isEqual from 'lodash/isEqual'
import template from 'lodash/template'
import { withRouter } from 'next/router'
import PropTypes from 'prop-types'

import marker from '@img/markerList.svg'
import markerListSelected from '@img/markerListSelected.svg'
import ensureMapLoaded from 'common/utils/ensureMapLoaded'
import classNames from './styles.module.scss'


const propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      place_id: PropTypes.string,
      name: PropTypes.string,
      rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  configMap: PropTypes.object,
  className: PropTypes.string,
  infoWindowContent: PropTypes.string,
  cardCount: PropTypes.number,
  schoolList: PropTypes.array,
  openAllMarkers: PropTypes.bool,
  boundsPadding: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  preventHover: PropTypes.bool,
  preventRedirect: PropTypes.bool,
  mapId: PropTypes.number,
  router: PropTypes.object.isRequired,
}

const defaultProps = {
  configMap: {},
  className: undefined,
  locations: [],
  infoWindowContent: undefined,
  cardCount: undefined,
  schoolList: [],
  openAllMarkers: false,
  boundsPadding: undefined,
  preventHover: false,
  preventRedirect: false,
  mapId: 1,
}

class MapMultipleMarkersContainer extends Component {
  mapRef = createRef()
  markers = []
  infoWindow = null
  infoWindows = []
  abortController = null
  event = null
  firstRender = false

  componentDidMount() {
    this.abortController = new AbortController()
    ensureMapLoaded().then(() => {
      // If component unmounted do not load the map
      if(!this.abortController?.signal.aborted) {
        this.event = google.maps.event
        this.firstRender = true
        this.renderMap(this.props)
      }
    })
  }

  componentWillUnmount() {
    this.cleanup()
  }

  shouldComponentUpdate(nextProps) {
    if(this.firstRender
        && ['locations', 'cardCount', 'mapId', 'preventRedirect', 'schoolList']
          .some(
            (prop) => !isEqual(this.props[prop], nextProps[prop]))
    ) {
      this.renderMap(nextProps)
    }
    return false
  }

  @autobind
  cleanup() {
    if(this.abortController) {
      this.abortController.abort()
    }

    this.removeMarkers()
    if(this.infoWindows.length) {
      this.infoWindows.forEach((iw) => {
        if(iw) {
          this.event.clearInstanceListeners(iw)
          iw.close()
        }
      })
      this.infoWindows = []
    }
    if(this.infoWindow) {
      this.event.clearInstanceListeners(this.infoWindow)
      this.infoWindow = null
    }
  }

  @autobind
  removeMarkers() {
    this.markers.forEach((marker) => {
      if(marker) {
        this.event.clearInstanceListeners(marker)
        marker.map = null
      }
    })
    this.markers = []
  }

  @autobind
  renderMap(props) {
    this.cleanup()

    // Create new AbortController for this render
    this.abortController = new AbortController()
    const { signal } = this.abortController

    const markerIcon = props.markerIcon || marker
    const selectedMarkerIcon = props.markerIconSelected || props.markerIcon || markerListSelected
    const mapOptions = {
      gestureHandling: 'cooperative',
      panControl: false,
      disableDefaultUI: true,
      streetViewControl: false,
      maxZoom: 16,
      minZoom: 0,
      keyboardShortcuts: true,
      mapId: process.env.GOOGLE_MAP_ID,
      ...props.configMap,
    }

    if(!this.map) {
      this.map = new google.maps.Map(this.mapRef.current, mapOptions)
    }

    this.bounds = new google.maps.LatLngBounds()

    const escapeAttr = (str) => String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    const setInfoWindowAriaLabel = () => {
      const container = this.mapRef.current
      if(!container) {
        return
      }
      container.querySelectorAll('.gm-style-iw').forEach((dialog) => {
        const wrapper = dialog.querySelector('.mapInfoWrapper')
        if(wrapper) {
          const name = wrapper.getAttribute('data-location-name') || ''
          dialog.setAttribute('aria-label', name ? `Details for ${name}` : 'Location details')
        }
      })
    }

    if(!this.infoWindow && !this.props.openAllMarkers) {
      this.infoWindow = new google.maps.InfoWindow()
    }
    if(this.infoWindow && !this.props.openAllMarkers) {
      google.maps.event.clearListeners(this.infoWindow, 'domready')
      this.infoWindow.addListener('domready', setInfoWindowAriaLabel)
    }

    if(this.props.openAllMarkers) {
      this.infoWindows = []
    }

    props.locations.forEach((location) => {
      let infoWindow = this.infoWindow
      if(this.props.openAllMarkers) {
        infoWindow = new google.maps.InfoWindow()
        infoWindow.addListener('domready', setInfoWindowAriaLabel)
        this.infoWindows.push(infoWindow)
      }

      const pinImg = document.createElement('img')
      pinImg.src = markerIcon

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(location.lat, location.lng),
        map: this.map,
        content: pinImg,
        title: location.name || 'Map marker',
      })

      this.markers.push(marker)
      this.bounds.extend(marker.position)

      const setContent = () => {
        const hasRedirect = !props.preventRedirect && location.url
        infoWindow.setContent(`
          <div class="mapInfoWrapper ${hasRedirect ? 'withLink' : ''}" data-location-name="${escapeAttr(location.name)}">
            ${template(location.tooltipContent || props.infoWindowContent)(location)}
          </div>
        `)
        infoWindow.open(this.map, marker)
        pinImg.src = selectedMarkerIcon
        if(hasRedirect) {
          setTimeout(() => {
            const el = this.mapRef.current.querySelector('.mapInfoWrapper')
            el
              && el.addEventListener('click', () => {
                const url = location.url.replace(/\/de\/|\/fr\//g, '/')
                this.props.router.push(url)
              })
          })
        }
      }

      if(this.props.openAllMarkers) {
        setContent()
      }

      if(!this.props.preventHover) {
        marker.addEventListener('mouseover', setContent, { signal })
        marker.addListener('click', setContent)
      }

      google.maps.event.addListener(infoWindow, 'closeclick', () => {
        pinImg.src = markerIcon
      })

      google.maps.event.addListener(infoWindow, 'content_changed', () => {
        pinImg.src = markerIcon
      })

      setTimeout(() => {
        const el = document.querySelector(`[data-map="${location.id}"]`)
        if(!el || this.abortController?.signal.aborted) {
          return
        }

        el.addEventListener('mouseover', () => {
          setContent()
        }, { signal })

        el.addEventListener('mouseout', () => {
          google.maps.event.trigger(marker, 'mouseout')
          infoWindow.close()
          pinImg.src = markerIcon
        }, { signal })
      }, 0)
    })

    this.map.fitBounds(this.bounds, this.props.boundsPadding)
  }

  render() {
    return (
      <div
        className={cx(
          classNames.MapWrapper,
          this.props.openAllMarkers && classNames.openAllMarkers,
          this.props.className,
        )}
      >
        <div
          ref={this.mapRef}
          className="mapMultiple"
          data-tap-disabled="true"
        />
      </div>
    )
  }
}

MapMultipleMarkersContainer.propTypes = propTypes
MapMultipleMarkersContainer.defaultProps = defaultProps

export default withRouter(MapMultipleMarkersContainer)
